import React, { useEffect, useState, useCallback, useMemo } from "react";
import AdminHOC from "../../../components/shared/HOC/AdminHOC";
import "../../../assets/custom-css/curriculum.css";
import { deleteBundle } from "../../../service/BundleService";
import CommonTable from "../../../components/shared/table/Table";
import { useNavigate } from "react-router-dom";
import usePagination from "../../../assets/hooks/PaginationHooks/usePagination";
import { useBundles } from "../../../assets/hooks/CurriculumHooks/useBundles";
import CustomTab from "../../../components/shared/tab/TabComponent";
import AddBundle from "./AddBundle";
import ConfirmDeletePopup from "../../../components/shared/confirmDeletePopup/ConfirmDeletePopup";
import { Row, Col } from "antd";
import { TAB_LIST } from "../../../assets/FormFeilds/Config/config";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../components/shared/pagination/messages";
import CustomButton from "../../../components/global/Button/CustomButton";
import { activeCurriculum } from "../../../utility/coloumnConstants";
import CustomInput from "../../../components/shared/Input/Custom-Input";
import LottieIcon from "../../../components/shared/lottieIcon/LottieIcon";
import searchIcon from "../../../assets/Search-Icon.svg";
import { createSearchDebounce } from "../../../assets/commonFunctions/CommonFunction";
import PageLoader from "../../../components/global/Loader/PageLoader";

const ActiveCurriculum = () => {
  const tableRef = React.useRef(null);

  const navigate = useNavigate();

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    totalElements,
    setTotalElements,
    setPageSize,
  } = usePagination();

  const { bundleList, getBundles } = useBundles();
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visiblePopover, setVisiblePopover] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [bundleToDelete, setBundleToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState("1");
  const [loading, setLoading] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [isInputHovered, setIsInputHovered] = useState(false);

  const fetchBundles = useCallback(
    async (searchQuery = search) => {
      setLoading(true);
      try {
        const response = await getBundles(currentPage, pageSize, isActive, searchQuery);
        if (response) setTotalElements(response.totalElements);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize, isActive, getBundles, setTotalElements],
  );

  useEffect(() => {
    const cleanup = createSearchDebounce(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchBundles(search);
      }
    });
    return cleanup;
  }, [search , currentPage,pageSize,isActive]);

  const processedBundles = useMemo(
    () =>
      bundleList?.map((bundle) => ({
        id: bundle.bundleId,
        bundleName: bundle.bundleName,
      })),
    [bundleList],
  );

  const handleAddNew = () => {
    setIsModalOpen((prev) => !prev);
  };

  const handleViewBundleClick = (id, name) => {
    navigate(`/bundles-history/${id}`, {
        state: { bundleName: name, isActive: isActive },
    });
  };

  const handleOpenConfirmDeletePopup = (bundle) => {
    setIsConfirmPopupOpen(true);
    setBundleToDelete(bundle);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDeleteBundle = async () => {
    setIsLoading(true);
    try {
      await deleteBundle(bundleToDelete?.id);
      showSuccessMessage("Curriculum deleted successfully!");
      await fetchBundles();
    } catch (error) {
      showErrorMessage("Error occurred while deleting the Bundle.");
    } finally {
      setIsLoading(false);
      setIsConfirmPopupOpen(false);
      setBundleToDelete(null);
    }
  };

  const handleTableChange = (pagination) => {
    if (pagination) {
      if (pagination.pageSize !== pageSize) {
        setPageSize(pagination.pageSize);
        setCurrentPage(1);
      } else {
        setCurrentPage(pagination.current);
      }
      const scrollableNode = tableRef.current?.querySelector(".ant-table-body");
      if (scrollableNode) {
        scrollableNode.scrollTo({ top: 0, behavior: "auto" });
      }
    }
  };

  const getTableProps = () => {
    switch (activeTab) {
      case "1":
        return {
          columns: activeCurriculum(
            visiblePopover,
            setVisiblePopover,
            hoveredButton,
            setHoveredButton,
            handleOpenConfirmDeletePopup,
            handleViewBundleClick,
            navigate,
            isActive,
          ),
          data: processedBundles,
          pagState: {
            current: currentPage,
            pageSize: pageSize,
            total: totalElements,
          },
          onChange: handleTableChange,
        };
      case "2":
        return {
          columns: activeCurriculum(
            visiblePopover,
            setVisiblePopover,
            hoveredButton,
            setHoveredButton,
            handleOpenConfirmDeletePopup,
            handleViewBundleClick,
            navigate,
            isActive,
          ),
          data: processedBundles,
          pagState: {
            current: currentPage,
            pageSize: pageSize,
            total: totalElements,
          },
          onChange: handleTableChange,
        };
      default:
        return { columns: [], data: [], pagState: {}, onChange: () => { } };
    }
  };

  const tableProps = getTableProps();

  const handleTabChange = (key) => {
    setActiveTab(key);
    setIsActive(key === "1");
    setCurrentPage(1);
    setSearch("");
  };

  return (
    <>
    <PageLoader visible={isLoading}/>
    <div className="p-6 flex flex-col border border-[#E8E8E8] rounded-[10px] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.05)] gap-6">
      <div className="flex flex-col gap-6">
        <h1 className="text-[#e74860] text-[1.75rem] font-normal">
          Curriculums
        </h1>

        <div>
          <Row>
            <Col span={10}>
              <CustomTab
                entries={TAB_LIST}
                activeKey={activeTab}
                onChange={handleTabChange}
              />
            </Col>

            <Col span={14}>
              <div className="flex gap-[10px] justify-end">
                <CustomInput
                  key={activeTab}
                  className="w-[14vw]"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  icon={
                    isInputHovered ? (
                      <LottieIcon
                        name="searchIcon"
                        size={24}
                        shouldPlay={true}
                      />
                    ) : (
                      <img src={searchIcon} alt="search" className="w-6 h-6" />
                    )
                  }
                  allowClear
                />
                <CustomButton onClick={handleAddNew} variant="primary" disabled={activeTab === "2"}>
                  Add Curriculum
                </CustomButton>

                <CustomButton
                  onClick={() => navigate(`/bundles/bundle-report?status=${isActive ? "active" : "inactive"}`)}
                  variant="primary"
                >
                  Report
                </CustomButton>
              </div>
            </Col>
            </Row>

              <div ref={tableRef}>
                <CommonTable
                  data={tableProps.data || []}
                  columns={tableProps.columns}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalElements,
                    showSizeChanger: true,
                  }}
                  onChange={tableProps.onChange}
                  scroll={{ y: "48vh", x:"max-content" }}
                  className="hide-scrollbar top-table-radius"
                  serverSide={true}
                  showLoader={loading}
                  tableLayout="fixed" 
                />
              </div>
          
        </div>
      </div>

      {/* Modals and Popups */}
      <AddBundle
        isModalOpen={isModalOpen}
        getBundles={fetchBundles}
        handleCloseModal={handleCloseModal}
        setLoading={setIsLoading}
      />

      <ConfirmDeletePopup
        isOpen={isConfirmPopupOpen}
        onClose={() => setIsConfirmPopupOpen(false)}
        onConfirm={handleDeleteBundle}
      />
    </div>
    </>
  );
};

export default AdminHOC(ActiveCurriculum);
